import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabase';

export default function GoogleSignUpPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [existingRole, setExistingRole] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let authResolved = false;
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const errorParam = searchParams.get('error') || hashParams.get('error');

        const processAuthenticatedUser = async (user: any) => {
            try {
                const userEmail = user?.email;

                if (!userEmail) {
                    throw new Error('Could not get email from Google account.');
                }

                setEmail(userEmail);

                const { data: teacherData, error: teacherError } = await supabase
                    .from('teachers')
                    .select('email')
                    .eq('email', userEmail)
                    .maybeSingle();

                if (teacherError) throw teacherError;

                if (teacherData) {
                    authResolved = true;
                    if (isMounted) {
                        setExistingRole('teacher');
                        setLoading(false);
                    }
                    return;
                }

                const { data: studentData, error: studentError } = await supabase
                    .from('students')
                    .select('email')
                    .eq('email', userEmail)
                    .maybeSingle();

                if (studentError) throw studentError;

                if (studentData) {
                    authResolved = true;
                    if (isMounted) {
                        setExistingRole('student');
                        setLoading(false);
                    }
                    return;
                }

                authResolved = true;
                if (isMounted) {
                    setExistingRole(null);
                    setLoading(false);
                }
            } catch (err: any) {
                console.error('Process user error:', err);
                authResolved = true;
                if (isMounted) {
                    setError(err.message || 'An error occurred during sign-in.');
                    setLoading(false);
                }
            }
        };
        
        if (errorParam) {
            localStorage.removeItem('pendingOAuthIntent');
            setError('Google sign-in was cancelled or failed.');
            setLoading(false);
            return;
        }

        const handleAuth = async () => {
            try {
                // In Supabase v2 with PKCE, the client automatically extracts the code from the URL 
                // and exchanges it for a session. GetSession() waits for this initial exchange to complete.
                const { data, error } = await supabase.auth.getSession();
                
                if (error) {
                    throw error;
                }

                if (data.session?.user) {
                    await processAuthenticatedUser(data.session.user);
                    localStorage.removeItem('pendingOAuthIntent');
                } else {
                    // Try waiting for onAuthStateChange to fire if getSession didn't return immediately
                    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
                            await processAuthenticatedUser(session.user);
                            localStorage.removeItem('pendingOAuthIntent');
                            subscription.unsubscribe();
                        }
                    });

                    // Set a timeout to show an error if session is still missing after 5 seconds
                    setTimeout(() => {
                        if (isMounted && !authResolved) {
                            authResolved = true;
                            setError('Could not establish an authenticated session. Please try again.');
                            setLoading(false);
                            subscription.unsubscribe();
                        }
                    }, 5000);
                }
            } catch (err: any) {
                console.error('Google session error:', err);
                localStorage.removeItem('pendingOAuthIntent');
                authResolved = true;
                if (isMounted) {
                    setError(err.message || 'An error occurred during sign-in.');
                    setLoading(false);
                }
            }
        };

        handleAuth();

        return () => {
            isMounted = false;
        };
    }, [searchParams]);

    const handleSelectRole = async (role: 'teacher' | 'student') => {
        setLoading(true);
        setError('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            
            if (!user) throw new Error('Not authenticated');

            if (role === 'teacher') {
                // Teacher profile completion already lives in the dashboard edit flow.
                localStorage.setItem('userRole', 'teacher');
                localStorage.removeItem('pendingOAuthIntent');
                navigate('/dashboard', { replace: true });
            } else {
                // For students, create profile automatically
                const { error: insertError } = await supabase.from('students').insert({
                    user_id: user.id,
                    name: user.user_metadata?.full_name || user.user_metadata?.name || 'Student',
                    email: user.email,
                    grade: '',
                    role: 'student',
                    avatar_url: user.user_metadata?.avatar_url || null,
                });

                if (insertError) {
                    // Check if already exists
                    if (insertError.message.includes('duplicate')) {
                        setError('A student account with this email already exists. Please login instead.');
                        setLoading(false);
                        return;
                    }
                    throw insertError;
                }

                localStorage.setItem('userRole', 'student');
                localStorage.setItem('studentName', user.user_metadata?.full_name || user.user_metadata?.name || 'Student');
                localStorage.removeItem('pendingOAuthIntent');
                navigate('/student-dashboard', { replace: true });
            }
        } catch (err: any) {
            console.error('Role selection error:', err);
            setError(err.message || 'An error occurred.');
            setLoading(false);
        }
    };

    const handleLoginAsTeacher = () => {
        localStorage.setItem('userRole', 'teacher');
        navigate('/dashboard', { replace: true });
    };

    const handleLoginAsStudent = () => {
        localStorage.setItem('userRole', 'student');
        navigate('/student-dashboard', { replace: true });
    };

    if (loading) {
        return (
            <div className="auth-page-shell" style={{ minHeight: '100vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #197fe6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                    <p>Processing your sign-in...</p>
                </div>
            </div>
        );
    }

    if (existingRole) {
        return (
            <div className="auth-page-shell" style={{ minHeight: '100vh' }}>
                <div className="page-panel auth-card" style={{ boxShadow: '0 20px 55px rgba(15,23,42,0.08)', border: '1px solid rgba(148,163,184,0.16)', maxWidth: '400px', width: '100%' }}>
                    <div className="auth-header" style={{ marginBottom: '1rem' }}>
                        <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', marginBottom: '1rem' }}>
                            !
                        </div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Account Already Exists</h2>
                        <p style={{ color: '#64748b', marginBottom: '0.5rem' }}>
                            An account with <strong>{email}</strong> already exists as a <strong>{existingRole}</strong>.
                        </p>
                    </div>
                    
                    <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                        <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                            You already have a {existingRole} account. Please login to continue.
                        </p>
                    </div>

                    {existingRole === 'teacher' ? (
                        <button className="btn primary" onClick={handleLoginAsTeacher} style={{ width: '100%' }}>
                            Continue as Teacher
                        </button>
                    ) : (
                        <button className="btn primary" onClick={handleLoginAsStudent} style={{ width: '100%' }}>
                            Continue as Student
                        </button>
                    )}

                    {error && (
                        <p style={{ color: '#ef4444', textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem' }}>{error}</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page-shell" style={{ minHeight: '100vh' }}>
                <div className="page-panel auth-card" style={{ boxShadow: '0 20px 55px rgba(15,23,42,0.08)', border: '1px solid rgba(148,163,184,0.16)', maxWidth: '450px', width: '100%' }}>
                    <div className="auth-header" style={{ marginBottom: '1rem' }}>
                        <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #197fe6, #8b5cf6)' }}>
                        G
                        </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Welcome to LankaTutor!</h2>
                    <p style={{ color: '#64748b', marginBottom: '0.5rem' }}>
                        Signed in as <strong>{email}</strong>
                    </p>
                    <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        How would you like to use LankaTutor?
                    </p>
                </div>

                <div className="auth-role-chooser">
                    <button 
                        className="btn primary" 
                        onClick={() => handleSelectRole('teacher')}
                        disabled={loading}
                        style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.75rem', height: '2.75rem', borderRadius: '0.9rem', background: 'rgba(255,255,255,0.18)', fontSize: '0.9rem', fontWeight: 800 }}>T</span>
                        <span style={{ fontWeight: 600 }}>I'm a Teacher</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>Create profile to teach students</span>
                    </button>

                    <button 
                        className="btn secondary" 
                        onClick={() => handleSelectRole('student')}
                        disabled={loading}
                        style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.75rem', height: '2.75rem', borderRadius: '0.9rem', background: 'rgba(25,127,230,0.08)', color: '#197fe6', fontSize: '0.9rem', fontWeight: 800 }}>S</span>
                        <span style={{ fontWeight: 600 }}>I'm a Student</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>Find teachers and start learning</span>
                    </button>
                </div>

                {error && (
                    <p style={{ color: '#ef4444', textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem' }}>{error}</p>
                )}

                <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                    You can only create one account per email address.
                </p>
            </div>
        </div>
    );
}
